import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts, cellDataKeys } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

export const evaluateStrikeCountCounts: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode, baseSnapshot } = context;
    if (!baseSnapshot) return null; // We need base time data to calculate Count

    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;
    
    const atmStrike = Math.round(spot / gap) * gap;
    const minStrikeBound = atmStrike - (activeRange * gap);
    const maxStrikeBound = atmStrike + (activeRange * gap);

    const baseSpot = baseSnapshot.spotPrice || baseSnapshot.indexLtp || baseSnapshot.index_ltp || spot;
    const baseAtmStrike = Math.round(baseSpot / gap) * gap;
    const baseMinStrikeBound = baseAtmStrike - (activeRange * gap);
    const baseMaxStrikeBound = baseAtmStrike + (activeRange * gap);

    const valKey = cellDataKeys[settingsMode] || 'oi';

    // Get flat options for Live
    const liveRawOpts = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOpts);
    
    // Get flat options for Base
    const baseRawOpts = extractSnapshotOptions(baseSnapshot, symbol, expiry) || [];
    const baseFlatOpts = ensureFlatOpts(baseRawOpts);

    if (liveFlatOpts.length === 0 || baseFlatOpts.length === 0) return null;

    // Filter and sort for base (09:16)
    const baseFiltered = baseFlatOpts.filter((opt: any) => opt.strike >= baseMinStrikeBound && opt.strike <= baseMaxStrikeBound);
    const baseSorted = [...baseFiltered].sort((a: any, b: any) => (b[valKey] || 0) - (a[valKey] || 0));

    const itmMap0916 = new Map<string, any>();
    for (const sr of baseSorted) {
        const key = `${sr.type}-${sr.strike}`;
        const isAtm = sr.strike === baseAtmStrike;
        if (isAtm) {
            if (!itmMap0916.has(key)) itmMap0916.set(key, sr);
            continue;
        }
        const isItm = sr.type === 'CE' ? sr.strike < baseSpot : sr.strike > baseSpot;
        if (isItm) {
            if (!itmMap0916.has(key)) itmMap0916.set(key, sr);
        }
    }
    const top5Itm0916 = Array.from(itmMap0916.values()).slice(0, 5);
    const strikes0916 = top5Itm0916.map((o: any) => o.strike).filter(Boolean);
    const min0916 = strikes0916.length > 0 ? Math.min(...strikes0916) : 0;
    const max0916 = strikes0916.length > 0 ? Math.max(...strikes0916) : 0;

    // Filter and sort for live
    const liveFiltered = liveFlatOpts.filter((opt: any) => opt.strike >= minStrikeBound && opt.strike <= maxStrikeBound);
    const liveSorted = [...liveFiltered].sort((a: any, b: any) => (b[valKey] || 0) - (a[valKey] || 0));

    const itmMapLive = new Map<string, any>();
    for (const sr of liveSorted) {
        const key = `${sr.type}-${sr.strike}`;
        const isAtm = sr.strike === atmStrike;
        if (isAtm) {
            if (!itmMapLive.has(key)) itmMapLive.set(key, sr);
            continue;
        }
        const isItm = sr.type === 'CE' ? sr.strike < spot : sr.strike > spot;
        if (isItm) {
            if (!itmMapLive.has(key)) itmMapLive.set(key, sr);
        }
    }
    const allItmLive = Array.from(itmMapLive.values());
    const top10ItmLive = allItmLive.slice(0, 10);
    const liveStrikes10 = top10ItmLive.map((o: any) => o.strike).filter(Boolean);

    const top5ItmLive = allItmLive.slice(0, 5);
    const top5StrikesLive = top5ItmLive.map((o: any) => o.strike).filter(Boolean);
    const liveMin = top5StrikesLive.length > 0 ? Math.min(...top5StrikesLive) : 0;
    const liveMax = top5StrikesLive.length > 0 ? Math.max(...top5StrikesLive) : 0;

    let aboveMin = 0, belowMin = 0;
    let aboveMax = 0, belowMax = 0;
    let aboveAtm = 0, belowAtm = 0;
    let liveAboveMin = 0, liveBelowMin = 0;
    let liveAboveMax = 0, liveBelowMax = 0;

    for (const strike of liveStrikes10) {
        if (min0916 > 0) {
            if (strike > min0916) aboveMin++;
            if (strike < min0916) belowMin++;
        }
        if (max0916 > 0) {
            if (strike > max0916) aboveMax++;
            if (strike < max0916) belowMax++;
        }
        if (atmStrike > 0) {
            if (strike > atmStrike) aboveAtm++;
            if (strike < atmStrike) belowAtm++;
        }
        if (liveMin > 0) {
            if (strike > liveMin) liveAboveMin++;
            if (strike < liveMin) liveBelowMin++;
        }
        if (liveMax > 0) {
            if (strike > liveMax) liveAboveMax++;
            if (strike < liveMax) liveBelowMax++;
        }
    }

    const isBuyOnDip = (liveAboveMin > liveBelowMin) && (liveBelowMax > liveAboveMax) && (belowAtm > aboveAtm);
    const isSellOnRise = (liveBelowMin > liveAboveMin) && (liveAboveMax > liveBelowMax) && (aboveAtm > belowAtm);

    let status = 'Sideways';
    if (isBuyOnDip) status = 'BULL';
    else if (isSellOnRise) status = 'BEAR';

    if (status === 'Sideways') return null;

    // Entry strike logic (ATM)
    let entryPrice = 0;
    const optionType = status === 'BULL' ? 'CE' : 'PE';
    
    const atmRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === optionType);

    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;

        if (entryPrice > 0) {
            return {
                trade_type: status as any,
                strike: atmStrike,
                option_type: optionType,
                entry_price: entryPrice,
                remarks: `${status === 'BULL' ? 'BUY ON DIP' : 'SELL ON RISE'} (ATM Above: ${aboveAtm} Below: ${belowAtm} | 09:16 Min: ${min0916} Max: ${max0916})`
            };
        }
    }

    return null;
};
